import { Box, Card, CardActionArea, CardContent, Chip, CircularProgress, Typography } from '@mui/material'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../providers/AuthProvider'
import { useAssignedExperiments } from '../../hooks/useExpertReviews'

/**
 * Expert-facing landing. Shows only experiments the signed-in user is
 * invited to review (via experiment_reviewers). Click through to the
 * two-step review UI.
 */
export default function MyReviewsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { rows, loading, load } = useAssignedExperiments(user?.id || '')

  useEffect(() => {
    load()
  }, [load])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.default' }}>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 800, color: 'primary.main' }}>
          My Reviews
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Experiments you've been invited to audit
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 8 }}>
            <Typography sx={{ fontSize: 36, mb: 1.5 }}>📋</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.75 }}>
              No experiments assigned
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When a researcher invites you to audit an experiment, it will show up here.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {rows.map((r) => (
              <Card
                key={r.experiment_id}
                variant="outlined"
                sx={{ borderRadius: 2, '&:hover': { borderColor: 'primary.main' } }}
              >
                <CardActionArea onClick={() => navigate(`/experiments/${r.experiment_id}/review`)}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'primary.main', mb: 0.25 }}>
                          {r.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                          Invited {new Date(r.invited_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Chip
                        label="Begin review →"
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: '#e0f2fe',
                          color: '#0369a1',
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}
